import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send, Clock, Globe, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const Contact = () => {
  const { i18n } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);

  const content = {
    fr: {
      title: "Contactez-Nous",
      subtitle: "Notre équipe est là pour répondre à toutes vos questions",
      addressTitle: "Adresse",
      address: "6205, Blvd. des Grandes Prairies\nSuite 215-216\nSaint-Léonard, QC H1P 1A2\nCanada",
      phoneTitle: "Téléphone",
      phone: "1 866-722-7007",
      phoneHours: "Lun-Ven : 9h-17h (EST)",
      emailTitle: "Email",
      emails: ["contact@marathon-edenlifeintl.org", "support@marathon-edenlifeintl.org"],
      followTitle: "Suivez-nous",
      formTitle: "Envoyez-nous un message",
      nameLabel: "Nom complet",
      namePlaceholder: "Jean Dupont",
      emailLabel: "Adresse email",
      emailPlaceholder: "votre@email.com",
      subjectLabel: "Sujet",
      subjectPlaceholder: "Comment pouvons-nous vous aider ?",
      subjects: [
        "Question générale",
        "Achat de billets",
        "Problème de paiement",
        "Programme d'affiliation",
        "Support technique",
        "Autre"
      ],
      messageLabel: "Votre message",
      messagePlaceholder: "Décrivez votre demande en détail...",
      sendButton: "Envoyer le message",
      sending: "Envoi en cours...",
      successMessage: "Message envoyé avec succès ! Nous vous répondrons dans les plus brefs délais.",
      responseTime: "Temps de réponse habituel",
      responseTimeValue: "24-48 heures ouvrables",
      languages: "Langues de support",
      languagesValue: "Français, English, Kreyòl"
    },
    en: {
      title: "Contact Us",
      subtitle: "Our team is here to answer all your questions",
      addressTitle: "Address",
      address: "6205 Blvd. des Grandes Prairies\nSuite 215-216\nSaint-Leonard, QC H1P 1A2\nCanada",
      phoneTitle: "Phone",
      phone: "1 866-722-7007",
      phoneHours: "Mon-Fri: 9am-5pm (EST)",
      emailTitle: "Email",
      emails: ["contact@marathon-edenlifeintl.org", "support@marathon-edenlifeintl.org"],
      followTitle: "Follow Us",
      formTitle: "Send us a message",
      nameLabel: "Full name",
      namePlaceholder: "John Doe",
      emailLabel: "Email address",
      emailPlaceholder: "your@email.com",
      subjectLabel: "Subject",
      subjectPlaceholder: "How can we help you?",
      subjects: [
        "General question",
        "Ticket purchase",
        "Payment issue",
        "Affiliate program",
        "Technical support",
        "Other"
      ],
      messageLabel: "Your message",
      messagePlaceholder: "Describe your request in detail...",
      sendButton: "Send message",
      sending: "Sending...",
      successMessage: "Message sent successfully! We will respond as soon as possible.",
      responseTime: "Typical response time",
      responseTimeValue: "24-48 business hours",
      languages: "Support languages",
      languagesValue: "French, English, Kreyòl"
    },
    ht: {
      title: "Kontakte Nou",
      subtitle: "Ekip nou la pou reponn tout kesyon ou yo",
      addressTitle: "Adrès",
      address: "6205 Blvd. des Grandes Prairies\nSuite 215-216\nSaint-Leonard, QC H1P 1A2\nKanada",
      phoneTitle: "Telefòn",
      phone: "1 866-722-7007",
      phoneHours: "Lendi-Vandredi: 9è-5è (EST)",
      emailTitle: "Imel",
      emails: ["contact@marathon-edenlifeintl.org", "support@marathon-edenlifeintl.org"],
      followTitle: "Swiv Nou",
      formTitle: "Voye yon mesaj ban nou",
      nameLabel: "Non konplè",
      namePlaceholder: "Jan Batis",
      emailLabel: "Adrès imel",
      emailPlaceholder: "imel@ou.com",
      subjectLabel: "Sijè",
      subjectPlaceholder: "Ki jan nou ka ede ou?",
      subjects: [
        "Kesyon jeneral",
        "Acha tikè",
        "Pwoblèm peman",
        "Pwogram afilyasyon",
        "Sipò teknik",
        "Lòt"
      ],
      messageLabel: "Mesaj ou",
      messagePlaceholder: "Dekri demann ou an detay...",
      sendButton: "Voye mesaj",
      sending: "Ap voye...",
      successMessage: "Mesaj voye avèk siksè! Nou pral reponn pi vit posib.",
      responseTime: "Tan repons tipik",
      responseTimeValue: "24-48 èdtan travay",
      languages: "Lang sipò",
      languagesValue: "Fransè, Anglè, Kreyòl"
    },
    es: {
      title: "Contáctenos",
      subtitle: "Nuestro equipo está aquí para responder todas sus preguntas",
      addressTitle: "Dirección",
      address: "6205 Blvd. des Grandes Prairies\nSuite 215-216\nSaint-Leonard, QC H1P 1A2\nCanadá",
      phoneTitle: "Teléfono",
      phone: "1 866-722-7007",
      phoneHours: "Lun-Vie: 9am-5pm (EST)",
      emailTitle: "Correo",
      emails: ["contact@marathon-edenlifeintl.org", "support@marathon-edenlifeintl.org"],
      followTitle: "Síguenos",
      formTitle: "Envíenos un mensaje",
      nameLabel: "Nombre completo",
      namePlaceholder: "Juan Pérez",
      emailLabel: "Correo electrónico",
      emailPlaceholder: "su@correo.com",
      subjectLabel: "Asunto",
      subjectPlaceholder: "¿Cómo podemos ayudarle?",
      subjects: [
        "Pregunta general",
        "Compra de boletos",
        "Problema de pago",
        "Programa de afiliados",
        "Soporte técnico",
        "Otro"
      ],
      messageLabel: "Su mensaje",
      messagePlaceholder: "Describa su solicitud en detalle...",
      sendButton: "Enviar mensaje",
      sending: "Enviando...",
      successMessage: "¡Mensaje enviado con éxito! Responderemos lo antes posible.",
      responseTime: "Tiempo de respuesta típico",
      responseTimeValue: "24-48 horas hábiles",
      languages: "Idiomas de soporte",
      languagesValue: "Francés, Inglés, Kreyòl"
    },
    pt: {
      title: "Entre em Contato",
      subtitle: "Nossa equipe está aqui para responder todas as suas perguntas",
      addressTitle: "Endereço",
      address: "6205 Blvd. des Grandes Prairies\nSuite 215-216\nSaint-Leonard, QC H1P 1A2\nCanadá",
      phoneTitle: "Telefone",
      phone: "1 866-722-7007",
      phoneHours: "Seg-Sex: 9h-17h (EST)",
      emailTitle: "Email",
      emails: ["contact@marathon-edenlifeintl.org", "support@marathon-edenlifeintl.org"],
      followTitle: "Siga-nos",
      formTitle: "Envie-nos uma mensagem",
      nameLabel: "Nome completo",
      namePlaceholder: "João Silva",
      emailLabel: "Endereço de email",
      emailPlaceholder: "seu@email.com",
      subjectLabel: "Assunto",
      subjectPlaceholder: "Como podemos ajudá-lo?",
      subjects: [
        "Pergunta geral",
        "Compra de bilhetes",
        "Problema de pagamento",
        "Programa de afiliados",
        "Suporte técnico",
        "Outro"
      ],
      messageLabel: "Sua mensagem",
      messagePlaceholder: "Descreva sua solicitação em detalhes...",
      sendButton: "Enviar mensagem",
      sending: "Enviando...",
      successMessage: "Mensagem enviada com sucesso! Responderemos o mais rápido possível.",
      responseTime: "Tempo de resposta típico",
      responseTimeValue: "24-48 horas úteis",
      languages: "Idiomas de suporte",
      languagesValue: "Francês, Inglês, Kreyòl"
    }
  };

  const t = content[i18n.language] || content.fr;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error(i18n.language === 'fr' ? 'Veuillez remplir tous les champs' : 'Please fill in all fields');
      return;
    }
    
    setSending(true);
    // Simulate sending
    setTimeout(() => {
      toast.success(t.successMessage);
      setFormData({ name: '', email: '', subject: '', message: '' });
      setSending(false);
    }, 1500);
  };

  return (
    <div className="min-h-screen pt-24 px-6 pb-12" style={{ background: 'linear-gradient(135deg, #F4F1DE 0%, #E8E3D3 100%)' }}>
      <div className="max-w-6xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="text-center mb-12">
            <Mail className="w-20 h-20 text-primary mx-auto mb-4" />
            <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              {t.title}
            </h1>
            <p className="text-lg text-gray-600">{t.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Contact Info */}
            <div className="space-y-6">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white/90 backdrop-blur-md rounded-2xl shadow-lg p-6"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-primary mb-2">{t.addressTitle}</h3>
                    <p className="text-gray-600 whitespace-pre-line">{t.address}</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white/90 backdrop-blur-md rounded-2xl shadow-lg p-6"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-secondary" />
                  </div>
                  <div>
                    <h3 className="font-bold text-primary mb-2">{t.phoneTitle}</h3>
                    <p className="text-gray-800 font-semibold">
                      <a href="tel:+18667227007" className="hover:text-primary">{t.phone}</a>
                    </p>
                    <p className="text-sm text-gray-500 mt-1">{t.phoneHours}</p>
                  </div>
                </div>
              </motion.div>

              {/* Haiti Contact Numbers */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.35 }}
                className="bg-gradient-to-br from-blue-600 to-red-600 rounded-2xl shadow-lg p-6 text-white"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-lg mb-3">📍 Contact Haïti</h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 bg-white/10 rounded-lg p-3">
                        <span className="bg-green-500 text-white text-xs font-bold px-2 py-1 rounded">Digi</span>
                        <a href="tel:+50939451425" className="font-semibold hover:underline">+509-3945-1425</a>
                      </div>
                      <div className="flex items-center gap-3 bg-white/10 rounded-lg p-3">
                        <span className="bg-blue-400 text-white text-xs font-bold px-2 py-1 rounded">Nat</span>
                        <a href="tel:+50956088028" className="font-semibold hover:underline">+509-5608-8028</a>
                      </div>
                    </div>
                    <p className="text-white/70 text-sm mt-3">
                      {i18n.language === 'fr' ? 'Pour nos amis en Haïti' : 
                       i18n.language === 'ht' ? 'Pou zanmi nou yo ann Ayiti' : 
                       'For our friends in Haiti'}
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-white/90 backdrop-blur-md rounded-2xl shadow-lg p-6"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-bold text-primary mb-2">{t.emailTitle}</h3>
                    {t.emails.map((email, i) => (
                      <p key={i} className="text-gray-600">
                        <a href={`mailto:${email}`} className="hover:text-primary">{email}</a>
                      </p>
                    ))}
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="bg-white/90 backdrop-blur-md rounded-2xl shadow-lg p-6"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-start space-x-3">
                    <Clock className="w-5 h-5 text-primary mt-1" />
                    <div>
                      <p className="text-sm text-gray-500">{t.responseTime}</p>
                      <p className="font-semibold text-gray-800">{t.responseTimeValue}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <Globe className="w-5 h-5 text-primary mt-1" />
                    <div>
                      <p className="text-sm text-gray-500">{t.languages}</p>
                      <p className="font-semibold text-gray-800">{t.languagesValue}</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
                className="bg-gradient-to-br from-primary to-accent rounded-2xl p-6 text-white"
              >
                <h3 className="font-bold mb-4 text-xl">{t.followTitle}</h3>
                <div className="flex space-x-4">
                  <a href="#" className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors" aria-label="Facebook">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  </a>
                  <a href="#" className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors" aria-label="Twitter">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
                  </a>
                  <a href="#" className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30 transition-colors" aria-label="Instagram">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                  </a>
                </div>
              </motion.div>
            </div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl p-8"
            >
              <div className="flex items-center space-x-3 mb-6">
                <MessageCircle className="w-8 h-8 text-primary" />
                <h2 className="text-2xl font-bold text-primary" style={{ fontFamily: 'Playfair Display, serif' }}>
                  {t.formTitle}
                </h2>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="name">{t.nameLabel}</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-2 h-12"
                    placeholder={t.namePlaceholder}
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="email">{t.emailLabel}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-2 h-12"
                    placeholder={t.emailPlaceholder}
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="subject">{t.subjectLabel}</Label>
                  <select
                    id="subject"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="mt-2 w-full h-12 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    required
                  >
                    <option value="">{t.subjectPlaceholder}</option>
                    {t.subjects.map((subject, i) => (
                      <option key={i} value={subject}>{subject}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <Label htmlFor="message">{t.messageLabel}</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="mt-2 min-h-[150px]"
                    placeholder={t.messagePlaceholder}
                    required
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={sending}
                  className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 h-12 rounded-full text-lg font-semibold"
                >
                  <Send className="w-5 h-5 mr-2" />
                  {sending ? t.sending : t.sendButton}
                </Button>
              </form>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;
